import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { 
  FaLayerGroup, FaQuestionCircle, FaRoad, FaCalendarDay, FaTh, FaRobot,
  FaBook, FaUtensils, FaCalendarAlt, FaCity, FaQuoteRight, FaComments,
  FaSpa, FaMicrophoneAlt, FaPenFancy, FaEye, FaHandshake, FaHourglassStart,
  FaChartLine, FaUsers, FaVolumeUp, FaMobileAlt, FaGlobeAsia, FaLock,
  FaCrown, FaInfinity, FaHeadset, FaSitemap, FaGlobe, FaRunning,
  FaCheckCircle, FaArrowRight, FaFacebookF, FaInstagram, FaTwitter, FaYoutube,
  FaLeaf, FaSmile, FaPlane, FaBriefcase, FaPalette, FaBrain, FaPencilAlt,
  FaClock, FaHeadphones, FaMobile, FaShieldAlt, FaStar, FaHeart
} from 'react-icons/fa';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits?: string[];
  link?: string;
}

const Features: React.FC = () => {
  const { user, logout } = useAuth();
  

  const coreLearningFeatures = [
    {
      icon: <FaLayerGroup className="text-white text-2xl" />,
      title: 'Interactive Flashcards',
      description: 'Master vocabulary with our intelligent flashcard system that adapts to your learning pace and focuses on words you need to practice most.',
      benefits: ['Spaced repetition algorithm', 'Audio pronunciations', 'Progress tracking', 'Customizable study sessions'],
      link: '/flashcards'
    },
    {
      icon: <FaQuestionCircle className="text-white text-2xl" />,
      title: 'Smart Quizzes',
      description: 'Test your knowledge with adaptive quizzes that get harder as you improve, ensuring you\'re always challenged at the right level.',
      benefits: ['Adaptive difficulty', 'Multiple question types', 'Detailed explanations', 'Performance analytics'],
      link: '/quiz'
    },
    {
      icon: <FaRoad className="text-white text-2xl" />,
      title: 'Learning Pathways',
      description: 'Follow structured learning journeys designed by native speakers, from beginner basics to advanced cultural content.',
      benefits: ['Progressive difficulty', 'Cultural context', 'Native speaker content', 'Rich multimedia lessons'],
      link: '/pathways'
    },
    {
      icon: <FaCalendarDay className="text-white text-2xl" />,
      title: 'Word of the Day',
      description: 'Build vocabulary gradually with daily words, complete with pronunciation, usage examples, and cultural context.',
      benefits: ['Daily new words', 'Audio pronunciation', 'Example sentences', 'Cultural insights'],
      link: '/word-of-the-day'
    },
    {
      icon: <FaTh className="text-white text-2xl" />,
      title: 'Alphabet Learning Grid',
      description: 'Master the Persian alphabet through our interactive grid system with visual associations and progressive lessons.',
      benefits: ['Visual learning', 'Interactive exercises', 'Audio pronunciation', 'Writing practice'],
      link: '/alphabet-grid'
    },
    {
      icon: <FaRobot className="text-white text-2xl" />,
      title: 'AI Learning Assistant',
      description: 'Get personalized help from our AI tutor that adapts to your learning style and provides instant feedback.',
      benefits: ['Personalized guidance', 'Instant feedback', 'Adaptive learning', '24/7 availability'],
      link: '/ai-bot'
    }
  ];

  const culturalFeatures = [
    {
      icon: <FaBook className="text-white text-2xl" />,
      title: 'Persian Poetry & Literature',
      description: 'Immerse yourself in classical Persian poetry, including works by Hafez, with translations and cultural explanations.',
      link: '/hafez'
    },
    {
      icon: <FaUtensils className="text-white text-2xl" />,
      title: 'Traditional Recipes & Cuisine',
      description: 'Learn about Persian cuisine through authentic recipes, cooking vocabulary, and cultural food traditions.',
      link: '/cultural/recipes'
    },
    {
      icon: <FaCalendarAlt className="text-white text-2xl" />,
      title: 'Holiday Celebrations',
      description: 'Discover Persian holidays and celebrations, learning vocabulary and cultural practices for each occasion.',
      link: '/cultural/holidays'
    },
    {
      icon: <FaCity className="text-white text-2xl" />,
      title: 'Tehran City Guide',
      description: 'Explore Tehran through interactive lessons about the city, its landmarks, and everyday life vocabulary.',
      link: '/cultural/tehran'
    },
    {
      icon: <FaQuoteRight className="text-white text-2xl" />,
      title: 'Persian Proverbs',
      description: 'Learn traditional Persian proverbs and their meanings, understanding cultural wisdom and expressions.',
      link: '/proverbs'
    },
    {
      icon: <FaComments className="text-white text-2xl" />,
      title: 'Cultural Dialogues',
      description: 'Practice real conversations with cultural context, learning appropriate responses and social customs.',
      link: '/cultural/dialogues'
    }
  ];



  const advancedFeatures = [
    {
      icon: <FaSpa className="text-white text-2xl" />,
      title: 'Mindful Learning Mode',
      description: 'Relaxing review sessions with ambient audio and stress-free learning environment.'
    },
    {
      icon: <FaMicrophoneAlt className="text-white text-2xl" />,
      title: 'Shadow Speaking',
      description: 'Practice pronunciation by comparing your voice with native speakers in real-time.'
    },
    {
      icon: <FaPenFancy className="text-white text-2xl" />,
      title: 'Writing Practice',
      description: 'Learn to write Persian script with guided exercises and stroke-by-stroke tutorials.'
    },
    {
      icon: <FaEye className="text-white text-2xl" />,
      title: 'Visual Learning',
      description: 'Enhance memory with visual associations, images, and mnemonic techniques.'
    },
    {
      icon: <FaHandshake className="text-white text-2xl" />,
      title: 'Taarof Mastery',
      description: 'Master Persian politeness and social customs through interactive scenarios.'
    },
    {
      icon: <FaHourglassStart className="text-white text-2xl" />,
      title: 'Three-Minute Persian',
      description: 'Quick, focused lessons perfect for busy schedules and micro-learning.'
    }
  ];

  const platformFeatures = [
    {
      icon: <FaChartLine className="text-white text-2xl" />,
      title: 'Progress Tracking',
      description: 'Monitor your learning journey with detailed analytics, streak tracking, and personalized insights.'
    },
    {
      icon: <FaUsers className="text-white text-2xl" />,
      title: 'Community Forum',
      description: 'Connect with fellow learners, ask questions, and share your progress in our supportive community.'
    },
    {
      icon: <FaVolumeUp className="text-white text-2xl" />,
      title: 'Audio Pronunciations',
      description: 'Perfect your pronunciation with native speaker audio for every word and phrase.'
    },
    {
      icon: <FaMobileAlt className="text-white text-2xl" />,
      title: 'Mobile Responsive',
      description: 'Learn on any device with our fully responsive design that works perfectly on phones, tablets, and computers.'
    },
    {
      icon: <FaGlobeAsia className="text-white text-2xl" />,
      title: 'Cultural Immersion',
      description: 'Go beyond language learning with deep cultural content that helps you understand Persian traditions and customs.'
    },
    {
      icon: <FaLock className="text-white text-2xl" />,
      title: 'Secure & Private',
      description: 'Your data is protected with enterprise-grade security and privacy controls.'
    }
  ];

  const subscriptionFeatures = [
    {
      icon: <FaCrown className="text-purple-600 text-2xl" />,
      title: 'Premium Content',
      description: 'Access exclusive lessons, advanced vocabulary, and premium cultural content.'
    },
    {
      icon: <FaInfinity className="text-purple-600 text-2xl" />,
      title: 'Unlimited Learning',
      description: 'No daily limits on words, lessons, or practice sessions.'
    },
    {
      icon: <FaRobot className="text-purple-600 text-2xl" />,
      title: 'AI Tutor Access',
      description: 'Full access to our AI learning assistant for personalized guidance.'
    },
    {
      icon: <FaHeadset className="text-purple-600 text-2xl" />,
      title: 'Priority Support',
      description: 'Get help faster with priority customer support and dedicated assistance.'
    }
  ];

  return (
    <>
      <SEO 
        title="Features | پیروز Pirooz - Complete Farsi Learning Platform"
        description="Discover all features of پیروز Pirooz: interactive Farsi lessons, vocabulary builder, pronunciation guides, cultural insights, progress tracking, and personalized learning paths for mastering Persian language."
        keywords={[
          'farsi learning features', 'persian language tools', 'interactive farsi lessons', 'farsi vocabulary builder',
          'persian pronunciation', 'farsi flashcards', 'cultural insights', 'persian grammar tools',
          'farsi progress tracking', 'personalized learning', 'farsi audio lessons'
        ]}
        canonicalUrl="/features"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Farsi Learning Platform Features",
          "description": "Complete overview of Vajak's Farsi learning features and tools",
          "url": "https://farsi-app.com/features",
          "mainEntity": {
            "@type": "SoftwareApplication",
            "name": "پیروز Pirooz",
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Interactive Farsi lessons",
              "Vocabulary builder",
              "Pronunciation guides", 
              "Cultural insights",
              "Progress tracking",
              "Personalized learning paths"
            ]
          }
        }}
      />
      <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              <span className="block text-white">Discover the Complete</span>
              <span className="block text-white" style={{ fontFamily: 'Noto Naskh Arabic, serif' }}>فارسی</span>
              <span className="block mt-2 text-white">Learning Experience</span>
            </h1>
            <p className="mt-6 max-w-lg mx-auto text-xl">
              From interactive flashcards to cultural immersion, explore every tool and feature designed to make your Persian learning journey effective, engaging, and culturally rich.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/register" className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-purple-700 bg-white hover:bg-gray-100 md:py-4 md:text-lg md:px-10 transition-all duration-300 hover:transform hover:scale-105">
                Start Learning
              </Link>
              <Link to="/pricing" className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-700 bg-opacity-60 hover:bg-opacity-70 md:py-4 md:text-lg md:px-10 transition-all duration-300 hover:transform hover:scale-105">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Core Learning Tools */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Core Learning Tools</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Our flagship features designed to accelerate your Farsi learning
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Proven methodologies and cutting-edge technology to help you master Persian.
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {coreLearningFeatures.map((feature, index) => (
                <div key={index} className="bg-white overflow-hidden shadow rounded-lg transition duration-300 ease-in-out hover:transform hover:-translate-y-1 hover:shadow-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                        {feature.icon}
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{feature.title}</h3>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-base text-gray-500">
                        {feature.description}
                      </p>
                      <ul className="mt-4 space-y-2">
                        {feature.benefits?.map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="flex items-start">
                            <div className="flex-shrink-0 text-purple-500">
                              <FaCheckCircle />
                            </div>
                            <p className="ml-2 text-sm text-gray-500">{benefit}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-6">
                      <Link to={feature.link || '#'} className="inline-flex items-center text-purple-600 hover:text-purple-500">
                        Try {feature.title}
                        <FaArrowRight className="ml-2" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Vocabulary Gardens */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Vocabulary Gardens</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Explore themed vocabulary collections
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Beautifully organized like Persian gardens, each representing different aspects of Persian language and culture.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Kitchen & Cooking */}
            <Link to="/vocabulary/kitchen" className="block rounded-lg overflow-hidden shadow-lg transition duration-300 ease-in-out hover:transform hover:scale-105" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("https://images.unsplash.com/photo-1601050690597-df0568f70950?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80")', height: '200px', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="h-full flex flex-col justify-end p-6">
                <h3 className="text-xl font-bold text-white">Kitchen & Cooking</h3>
                <p className="mt-2 text-gray-200">Essential cooking vocabulary and culinary terms</p>
              </div>
            </Link>

            {/* Family & Relationships */}
            <Link to="/vocabulary/family" className="block rounded-lg overflow-hidden shadow-lg transition duration-300 ease-in-out hover:transform hover:scale-105" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("https://images.unsplash.com/photo-1529333166447-4f1c5b1b0e9b?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80")', height: '200px', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="h-full flex flex-col justify-end p-6">
                <h3 className="text-xl font-bold text-white">Family & Relationships</h3>
                <p className="mt-2 text-gray-200">Family terms and relationship vocabulary</p>
              </div>
            </Link>

            {/* Nature & Environment */}
            <Link to="/vocabulary/nature" className="block rounded-lg overflow-hidden shadow-lg transition duration-300 ease-in-out hover:transform hover:scale-105" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80")', height: '200px', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="h-full flex flex-col justify-end p-6">
                <h3 className="text-xl font-bold text-white">Nature & Environment</h3>
                <p className="mt-2 text-gray-200">Nature and environmental terminology</p>
              </div>
            </Link>

            {/* Emotions & Feelings */}
            <Link to="/vocabulary/feelings" className="block rounded-lg overflow-hidden shadow-lg transition duration-300 ease-in-out hover:transform hover:scale-105" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("https://images.unsplash.com/photo-1542204637-e67bc7d41e48?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80")', height: '200px', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="h-full flex flex-col justify-end p-6">
                <h3 className="text-xl font-bold text-white">Emotions & Feelings</h3>
                <p className="mt-2 text-gray-200">Express emotions and feelings in Persian</p>
              </div>
            </Link>

            {/* City & Urban Life */}
            <Link to="/vocabulary/city" className="block rounded-lg overflow-hidden shadow-lg transition duration-300 ease-in-out hover:transform hover:scale-105" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("https://images.unsplash.com/photo-1484557985045-edf81250fe3f?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80")', height: '200px', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="h-full flex flex-col justify-end p-6">
                <h3 className="text-xl font-bold text-white">City & Urban Life</h3>
                <p className="mt-2 text-gray-200">Urban vocabulary and city life terms</p>
              </div>
            </Link>

            {/* Travel & Transportation */}
            <Link to="/vocabulary/travel" className="block rounded-lg overflow-hidden shadow-lg transition duration-300 ease-in-out hover:transform hover:scale-105" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("https://images.unsplash.com/photo-1503917988258-f87a78e3c995?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80")', height: '200px', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="h-full flex flex-col justify-end p-6">
                <h3 className="text-xl font-bold text-white">Travel & Transportation</h3>
                <p className="mt-2 text-gray-200">Travel and transportation vocabulary</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Cultural Immersion */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Cultural Immersion</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Go beyond language learning
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Immerse yourself in Persian culture, traditions, and heritage through interactive content.
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {culturalFeatures.map((feature, index) => (
                <div key={index} className="bg-gray-50 rounded-lg overflow-hidden shadow">
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                        {feature.icon}
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{feature.title}</h3>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-base text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Learning Features */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Advanced Learning Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Take your learning to the next level
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our advanced tools and techniques designed for serious learners.
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {advancedFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-lg overflow-hidden shadow">
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                        {feature.icon}
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{feature.title}</h3>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-base text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Platform Features */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Platform Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need for a seamless experience
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Effective learning across all devices with our comprehensive platform.
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {platformFeatures.map((feature, index) => (
                <div key={index} className="bg-gray-50 rounded-lg overflow-hidden shadow">
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                        {feature.icon}
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{feature.title}</h3>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-base text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Premium Features */}
      <div className="py-12 bg-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-200 font-semibold tracking-wide uppercase">Premium Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight sm:text-4xl">
              Unlock unlimited learning potential
            </p>
            <p className="mt-4 max-w-2xl text-xl text-purple-100 lg:mx-auto">
              Access exclusive content and features with our premium subscription.
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {subscriptionFeatures.map((feature, index) => (
                <div key={index} className="bg-white bg-opacity-10 rounded-lg overflow-hidden shadow backdrop-filter backdrop-blur-sm">
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-white rounded-md p-3">
                        {feature.icon}
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <h3 className="text-lg font-medium">{feature.title}</h3>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-base text-purple-100">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/subscription" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-purple-700 bg-white hover:bg-gray-100">
              Upgrade to Premium
            </Link>
          </div>
        </div>
      </div>

      {/* Our Learning Approach */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Our Learning Approach</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              How we help you master Farsi
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              We believe that effective language learning happens through a combination of structured practice, cultural immersion, and community support.
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {/* Structured Learning */}
              <div className="bg-white rounded-lg overflow-hidden shadow">
                <div className="p-6 text-center">
                  <div className="flex justify-center">
                    <div className="flex-shrink-0 bg-purple-100 rounded-full p-4">
                      <FaSitemap className="text-purple-600 text-2xl" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900">Structured Learning</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Follow carefully designed pathways that build your skills progressively.
                    </p>
                  </div>
                </div>
              </div>

              {/* Cultural Context */}
              <div className="bg-white rounded-lg overflow-hidden shadow">
                <div className="p-6 text-center">
                  <div className="flex justify-center">
                    <div className="flex-shrink-0 bg-purple-100 rounded-full p-4">
                      <FaGlobe className="text-purple-600 text-2xl" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900">Cultural Context</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Learn language within its cultural framework for deeper understanding.
                    </p>
                  </div>
                </div>
              </div>

              {/* Active Practice */}
              <div className="bg-white rounded-lg overflow-hidden shadow">
                <div className="p-6 text-center">
                  <div className="flex justify-center">
                    <div className="flex-shrink-0 bg-purple-100 rounded-full p-4">
                      <FaRunning className="text-purple-600 text-2xl" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900">Active Practice</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Engage with interactive tools that reinforce learning through practice.
                    </p>
                  </div>
                </div>
              </div>

              {/* Community Support */}
              <div className="bg-white rounded-lg overflow-hidden shadow">
                <div className="p-6 text-center">
                  <div className="flex justify-center">
                    <div className="flex-shrink-0 bg-purple-100 rounded-full p-4">
                      <FaUsers className="text-purple-600 text-2xl" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900">Community Support</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Connect with fellow learners for motivation and shared learning.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Proven Results</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Why learners choose Vajak
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
              {/* Vocabulary Retention */}
              <div className="bg-white rounded-lg overflow-hidden shadow text-center py-8 transition duration-300 ease-in-out hover:transform hover:-translate-y-1">
                <div className="text-5xl font-bold text-purple-600">95%</div>
                <div className="mt-2 text-lg font-medium text-gray-900">Vocabulary Retention</div>
                <p className="mt-2 text-base text-gray-500">
                  Our spaced repetition system ensures long-term retention
                </p>
              </div>

              {/* User Satisfaction */}
              <div className="bg-white rounded-lg overflow-hidden shadow text-center py-8 transition duration-300 ease-in-out hover:transform hover:-translate-y-1">
                <div className="text-5xl font-bold text-purple-600">98%</div>
                <div className="mt-2 text-lg font-medium text-gray-900">User Satisfaction</div>
                <p className="mt-2 text-base text-gray-500">
                  Learners love our comprehensive approach to Farsi
                </p>
              </div>

              {/* Learning Speed */}
              <div className="bg-white rounded-lg overflow-hidden shadow text-center py-8 transition duration-300 ease-in-out hover:transform hover:-translate-y-1">
                <div className="text-5xl font-bold text-purple-600">3x</div>
                <div className="mt-2 text-lg font-medium text-gray-900">Learning Speed</div>
                <p className="mt-2 text-base text-gray-500">
                  Faster progress compared to traditional methods
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8 lg:py-24">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              <span className="block">Ready to Start Your</span>
              <span className="block" style={{ fontFamily: 'Noto Naskh Arabic, serif' }}>فارسی</span>
              <span className="block mt-2">Learning Journey?</span>
            </h2>
            <p className="mt-6 max-w-lg mx-auto text-xl">
              Join thousands of learners who are already mastering Persian with our comprehensive platform.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/register" className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-purple-700 bg-white hover:bg-gray-100 md:py-4 md:text-lg md:px-10 transition-all duration-300 hover:transform hover:scale-105">
                Start Free Trial
              </Link>
              <Link to="/pricing" className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-700 bg-opacity-60 hover:bg-opacity-70 md:py-4 md:text-lg md:px-10 transition-all duration-300 hover:transform hover:scale-105">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-gray-300 tracking-wider uppercase">About Us</h3>
              <ul className="mt-4 space-y-4">
                <li><Link to="/about" className="text-base text-gray-400 hover:text-white">Our Mission</Link></li>
                <li><Link to="/team" className="text-base text-gray-400 hover:text-white">Team</Link></li>
                <li><Link to="/careers" className="text-base text-gray-400 hover:text-white">Careers</Link></li>
                <li><Link to="/press" className="text-base text-gray-400 hover:text-white">Press</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-300 tracking-wider uppercase">Pricing</h3>
              <ul className="mt-4 space-y-4">
                <li><Link to="/pricing" className="text-base text-gray-400 hover:text-white">Free Plan</Link></li>
                <li><Link to="/pricing" className="text-base text-gray-400 hover:text-white">Premium</Link></li>
                <li><Link to="/pricing" className="text-base text-gray-400 hover:text-white">Student Discount</Link></li>
                <li><Link to="/pricing" className="text-base text-gray-400 hover:text-white">Compare Plans</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-300 tracking-wider uppercase">Help</h3>
              <ul className="mt-4 space-y-4">
                <li><Link to="/help" className="text-base text-gray-400 hover:text-white">FAQs</Link></li>
                <li><Link to="/contact" className="text-base text-gray-400 hover:text-white">Contact Support</Link></li>
                <li><Link to="/community" className="text-base text-gray-400 hover:text-white">Community</Link></li>
                <li><Link to="/feedback" className="text-base text-gray-400 hover:text-white">Feedback</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-300 tracking-wider uppercase">Contact</h3>
              <ul className="mt-4 space-y-4">
                <li><Link to="/contact" className="text-base text-gray-400 hover:text-white">Email Us</Link></li>
                <li><a href="#" className="text-base text-gray-400 hover:text-white">Twitter</a></li>
                <li><a href="#" className="text-base text-gray-400 hover:text-white">Instagram</a></li>
                <li><a href="#" className="text-base text-gray-400 hover:text-white">Facebook</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 border-t border-gray-700 pt-8 md:flex md:items-center md:justify-between">
            <div className="flex space-x-6 md:order-2">
              <a href="#" className="text-gray-400 hover:text-gray-300">
                <span className="sr-only">Facebook</span>
                <FaFacebookF />
              </a>
              <a href="#" className="text-gray-400 hover:text-gray-300">
                <span className="sr-only">Instagram</span>
                <FaInstagram />
              </a>
              <a href="#" className="text-gray-400 hover:text-gray-300">
                <span className="sr-only">Twitter</span>
                <FaTwitter />
              </a>
              <a href="#" className="text-gray-400 hover:text-gray-300">
                <span className="sr-only">YouTube</span>
                <FaYoutube />
              </a>
            </div>
            <p className="mt-8 text-base text-gray-400 md:mt-0 md:order-1">
              &copy; 2023 Vajak. All rights reserved.
            </p>
          </div>
        </div>
      </footer>


    </div>
    </>
  );
};

export default Features; 